import { createContext, useContext } from 'react';
import type { AssignedLevel } from '../../../shared/constants/authorizationGroups';

export interface UserInfo {
  email: string;
  name?: string;
  sub?: string; // Cognito subject ID - used as actor_id for AgentCore
  groups?: string[]; // Every group in cognito:groups and custom:groups, for display
  assignedLevel: AssignedLevel | null; // From the level groups in cognito:groups; null is no level
  roles: string[]; // opspilot-admin from cognito:groups, msreportingadmin from either claim
}

// View-as previews a level, never a group or a role (X-OpsPilot-View-As)
export type ViewAsRole = AssignedLevel;

export interface AuthContextType {
  isAuthenticated: boolean;
  user: UserInfo | null;
  idToken: string | null;
  isAdmin: boolean;
  isRealAdmin: boolean;
  viewAsRole: ViewAsRole | null;
  setViewAsRole: (role: ViewAsRole | null) => void;
  login: () => void;
  logout: () => void;
  refreshCredentials: () => Promise<void>;
  hasAdminAccess: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
